import { Breadcrumb, Button, Spin, Row, Col } from 'antd';
import { Option } from 'antd/es/mentions';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchWorkOrderReport } from '../../slices/work-order';
import {
  fetchCustomerDropdown,
  resetCurrentCustomerPage,
} from '../../slices/customer';
import AutoOpenSelect from '../SelectComponent/AutoOpenSelect';
import {
  fetchDieProfileDropdown,
  resetDieProfileData,
} from '../../slices/die-profile';
import { debounce } from 'lodash';
import { Link, useNavigate } from 'react-router-dom';
import { ReactComponent as Back } from '../../Images/Back.svg';

const WorkOrderReport = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchDieProfile, setSearchDieProfile] = useState('');
  const [btnLoading, setBtnLoading] = useState(false);
  const [orderData, setOrderData] = useState([]);

  const [page, setPage] = useState(1);
  const [pagesize, setPageSize] = useState(100);
  const [loading, setLoading] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [selectedDieId, setSelectedDieId] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null); // 'customer' or 'die'
  const [selectedCustomerName, setSelectedCustomerName] = useState('');
  const [selectedDieName, setSelectedDieName] = useState('');
  const [showDropdownLoader, setShowDropdownLoader] = useState(false);
  const [forceOpen, setForceOpen] = useState(false);
  const { list: dieProfileDropdown } = useSelector(state => state.dieProfile);
  const {
    loading: dieProfileLoading,
    currentPage: dieProfileCurrentPage,
    hasMore: dieProfileHasMore,
  } = useSelector(state => state.dieProfile);

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      backgroundColor: '#f3f4f6',
      minHeight: '100vh',
    },
    filterRow: {
      display: 'flex',
      flexDirection: 'row',
      padding: '16px',
      gap: '16px',
    },
    filterBox: {
      width: '50%',
      backgroundColor: 'white',
      padding: '16px',
      borderRadius: '4px',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    },
    filterTitle: {
      color: 'rgb(16, 19, 22)',
      marginBottom: '16px',
    },
    filterInputContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '8px',
    },
    selectInput: {
      border: '1px solid rgb(169, 190, 222)',
      borderRadius: '4px',
      padding: '8px 12px',
      flexGrow: 1,
    },
    viewButton: {
      backgroundColor: '#3b82f6',
      color: 'white',
      padding: '8px 16px',
      borderRadius: '4px',
      border: 'none',
      cursor: 'pointer',
    },
    resultBar: {
      paddingLeft: '16px',
      paddingRight: '16px',
      paddingTop: '8px',
      paddingBottom: '8px',
      backgroundColor: '#e5e7eb',
      color: ' #4b5563',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    dateTag: {
      backgroundColor: '#1e40af',
      color: 'white',
      paddingLeft: '8px',
      paddingRight: '8px',
      paddingTop: '4px',
      paddingBottom: '4px',
      borderRadius: '4px',
      fontSize: '12px',
    },
    ButtonContainer: {
      display: 'flex',
      gap: '8px',
    },
    actionButton: {
      backgroundColor: '#10b981',
      color: 'white',
      paddingLeft: '16px',
      paddingRight: '16px',
      paddingTop: '4px',
      paddingBottom: '4px',
      borderRadius: '4px',
      fontSize: '14px',
      border: 'none',
      cursor: 'pointer',
    },
    tableContainer: {
      padding: '16px',
      overflowX: 'auto',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      backgroundColor: 'white',
    },
    tableHeaderRow: {
      backgroundColor: '#f9fafb',
    },
    tableHeaderCell: {
      border: '1px solid #d1d5db',
      padding: '8px',
      textAlign: 'center',
    },
    tableDataCell: {
      border: '1px solid #d1d5db',
      padding: '8px',
      textAlign: 'center',
    },
    tableRowEven: {
      backgroundColor: 'white',
    },
    tableRowOdd: {
      backgroundColor: '#f9fafb',
    },
    loadingCell: {
      border: '1px solid #d1d5db',
      padding: '16px',
      textAlign: 'center',
    },
  };

  useEffect(() => {
    dispatch(fetchCustomerDropdown({ page: 1, pagesize: 1000 }));
    dispatch(fetchDieProfileDropdown({ page: 1, pagesize: 50 }));
  }, [dispatch]);

  const fetchWorkOrderData = () => {
    setLoading(true);
    setBtnLoading(true);

    const params = {
      page,
      pagesize,
    };

    if (activeFilter === 'customer' && selectedCustomerId) {
      params.customer_id = selectedCustomerId;
    } else if (activeFilter === 'die' && selectedDieId) {
      params.die_id = selectedDieId;
    } else {
      setLoading(false);
      setBtnLoading(false);
      return;
    }
    dispatch(fetchWorkOrderReport(params))
      .then(response => {
        setOrderData(response?.payload?.data || []);
      })
      .catch(error => {
        console.error('Error fetching work order report:', error);
        setOrderData([]);
      })
      .finally(() => {
        setLoading(false);
        setBtnLoading(false);
      });
  };

  const { list: customerList } = useSelector(state => state.customer);

  const handleSearchCustomer = value => {
    if (value) {
      dispatch(fetchCustomerDropdown({ page: 1, pagesize: 1000, search: value }));
    } else {
      dispatch(fetchCustomerDropdown({ page: 1, pagesize: 1000 }));
      dispatch(resetCurrentCustomerPage());
    }
  };

  const debouncedSearchDieProfile = debounce((value, dispatch) => {
    dispatch(resetDieProfileData()); // Reset the list when searching
    dispatch(
      fetchDieProfileDropdown({
        page: 1,
        pagesize: 50,
        search: value || '',
      }),
    );
  }, 300);

  const [dievalue, setDievalue] = useState('');

  const handleCustomerChange = value => {
    if (value && value.key) {
      setSelectedCustomerId(value.key);
      setSelectedCustomerName(value.label);
      setSelectedDieId(null);
      setSelectedDieName('');
      setActiveFilter('customer');
    }
  };

  const handleDieProfileChange = value => {
    if (value && (value.key || value.value)) {
      const dieId = value.key || value.value || value;
      setSelectedDieId(dieId);
      setSelectedDieName(value.label || dievalue);
      setSelectedCustomerId(null);
      setSelectedCustomerName('');
      setActiveFilter('die');
    }
  };

  const handleSearchDieProfile = value => {
    setSearchDieProfile(value);
    if (value) {
      debouncedSearchDieProfile(value, dispatch);
    } else {
      dispatch(resetDieProfileData());
      dispatch(fetchDieProfileDropdown({ page: 1, pagesize: 500 }));
    }
  };

  const onScrollDieProfile = event => {
    const target = event.target;

    if (
      !dieProfileLoading &&
      dieProfileHasMore &&
      target.scrollHeight - target.scrollTop - target.clientHeight < 50
    ) {
      const nextPage = dieProfileCurrentPage + 1;

      dispatch(
        fetchDieProfileDropdown({
          page: nextPage,
          pagesize: 50,
          search: searchDieProfile || '',
        }),
      );
    }
  };

  const handleViewClick = () => {
    fetchWorkOrderData();
  };

  const handleClearCustomer = () => {
    setSelectedCustomerId(null);
    setSelectedCustomerName('');
    if (activeFilter === 'customer') {
      setActiveFilter(null);
      setOrderData([]);
    }
  };

  const handleClearDie = () => {
    setSelectedDieId(null);
    setSelectedDieName('');
    setSearchDieProfile('');
    setDievalue('');
    if (activeFilter === 'die') {
      setActiveFilter(null);
      setOrderData([]);
    }
    dispatch(resetDieProfileData());
    dispatch(fetchDieProfileDropdown({ page: 1, pagesize: 50 }));
  };
  const handlePrint = async () => {
    try {
      setLoading(true);
      const params = {
        page: 1,
        pagesize: 10000, 
      };

      if (activeFilter === 'customer' && selectedCustomerId) {
        params.customer_id = selectedCustomerId;
      } else if (activeFilter === 'die' && selectedDieId) {
        params.die_id = selectedDieId;
      } else {
        return;
      }

      const response = await dispatch(fetchWorkOrderReport(params));
      const allData = response?.payload?.data || [];
      const printWindow = window.open('', '_blank');
      const titleText = activeFilter === 'customer' ? selectedCustomerName : `Profile ${selectedDieName}`;
      printWindow.document.write(`
      <html>
        <head>
        <title>Work Order Report</title>
          <style>
            @media print {
              body {
                font-family: Arial, sans-serif;
                margin: 0;
                padding: 20px;
                 text-align:center;
           
              }
              .print-table {
                width: 100%;
                border-collapse: collapse;
                margin-bottom: 20px;
              }
              .print-table th, .print-table td {
                border: 1px solid #ddd;
                padding: 8px;
                text-align: left;
              }
              .print-table th {
                background-color: #12406a;
                position: sticky;
                color:white;
                top: 0;
              }
              .print-table tr:nth-child(even) {
                background-color: #f9f9f9;
              }
              .total-row {
                font-weight: bold;
                background-color: #e6e6e6 !important;
              }
              .table-container {
                max-height: none !important;
                overflow: visible !important;
              }
              img {
                max-width: 70px;
                max-height: 50px;
                object-fit: contain;
              }
              @page {
                size: landscape;
                 margin: 10mm;
                 padding: 20px;
              }
            }
          </style>
        </head>
        <body>
          <h2>Showing Result For : ${titleText}</h2>
          <div class="table-container">
            <table class="print-table">
              ${generateTableHeader()}
              ${generateTableBody(allData)}
              ${generateTotalRow(allData)}
            </table>
          </div>
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                window.close();
              }, 200);
            };
          </script>
        </body>
      </html>
    `);

      printWindow.document.close();
    } catch (error) {
      console.error('Error during printing:', error);
    } finally {
      setLoading(false);
    }
  };
  const generateTableHeader = () => {
    return `
    <thead>
      <tr>
        <th rowspan="2">Sr. No.</th>
        ${activeFilter === 'die' ? '<th rowspan="2">Customer Name</th>' : ''}
        <th rowspan="2">Work Order No</th>
        <th rowspan="2">Work Order Date - PO NO</th>
        <th rowspan="2">Profile No</th>
        <th rowspan="2">Profile Image</th>
        <th rowspan="2">Customer<br/> Reference<br/> Number</th>
        <th rowspan="2">Length(mm)</th>
        <th colspan="2">Order</th>
        <th colspan="2">Dispatched</th>
        <th colspan="2">Packed</th>
        ${activeFilter !== 'die' ? '<th colspan="2">Warehouse Packed</th>' : ''}
        <th colspan="2">Pending</th>
        <th rowspan="2">Remarks</th>
      </tr>
      <tr>
        <th>PCS</th>
        <th>KGS</th>
        <th>PCS</th>
        <th>KGS</th>
        <th>PCS</th>
        <th>KGS</th>
        ${activeFilter !== 'die' ? '<th>PCS</th><th>KGS</th>' : ''}
        <th>PCS</th>
        <th>KGS</th>
      </tr>
    </thead>
  `;
  };

  const generateTableBody = (data) => {
    const groupedData = groupData(data);

    let html = '<tbody>';
    let srNo = 1;

    Object.entries(groupedData).forEach(([workorder_no, rows]) => {
      rows.forEach((row, idx) => {
        html += `
        <tr ${srNo % 2 === 0 ? 'style="background-color: #f9f9f9;"' : ''}>
          ${idx === 0 ? `<td rowspan="${rows.length}">${srNo++}</td>` : ''}
          ${idx === 0 && activeFilter === 'die' ?
            `<td rowspan="${rows.length}">${row.customer_name || 'N/A'}</td>` : ''}
          ${idx === 0 ? `
            <td rowspan="${rows.length}">${row.workorder_no}</td>
            <td rowspan="${rows.length}">${row.workorder_date}</td>
          ` : ''}
          <td>${row.section || ''}</td>
          <td>${row.profile_image && row.profile_image.startsWith('https://') ? `<img src="${row.profile_image}" alt="Die Diagram" style="max-width:70px;max-height:50px;object-fit:contain;">` : ''}</td>
        <td style="width:50px !important;">${row.customer_reference_number || ''}</td>

          <td>${row.length || ''}</td>
          <td>${row.order_pcs || ''}</td>
          <td>${row.order_kgs ? row.order_kgs.toFixed(3) : ''}</td>
          <td>${row.dispatched_pcs || ''}</td>
          <td>${row.dispatched_kgs ? row.dispatched_kgs.toFixed(3) : ''}</td>
          <td>${row.packed_pcs || ''}</td>
          <td>${row.packed_kgs ? row.packed_kgs.toFixed(3) : ''}</td>
          ${activeFilter !== 'die' ? `
            <td>${row.warehouse_pcs || ''}</td>
            <td>${row.warehouse_kgs ? row.warehouse_kgs.toFixed(3) : ''}</td>
          ` : ''}
          <td>${row.pending_pcs || ''}</td>
          <td>${row.pending_kgs ? row.pending_kgs.toFixed(3) : ''}</td>
          <td>${row.remark || ''}</td>
        </tr>
      `;
      });
    });

    html += '</tbody>';
    return html;
  };

  const generateTotalRow = (data) => {
    const totals = {
      order_pcs: 0,
      order_kgs: 0,
      dispatched_pcs: 0,
      dispatched_kgs: 0,
      packed_pcs: 0,
      packed_kgs: 0,
      warehouse_pcs: 0,
      warehouse_kgs: 0,
      pending_pcs: 0,
      pending_kgs: 0
    };

    data.forEach(row => {
      totals.order_pcs += Number(row.order_pcs) || 0;
      totals.order_kgs += Number(row.order_kgs) || 0;
      totals.dispatched_pcs += Number(row.dispatched_pcs) || 0;
      totals.dispatched_kgs += Number(row.dispatched_kgs) || 0;
      totals.packed_pcs += Number(row.packed_pcs) || 0;
      totals.packed_kgs += Number(row.packed_kgs) || 0;
      totals.warehouse_pcs += Number(row.warehouse_pcs) || 0;
      totals.warehouse_kgs += Number(row.warehouse_kgs) || 0;
      totals.pending_pcs += Number(row.pending_pcs) || 0;
      totals.pending_kgs += Number(row.pending_kgs) || 0;
    });

    return `
    <tfoot>
      <tr class="total-row">
        <td colspan="${activeFilter === 'die' ? 8 : 7}"><strong>Total</strong></td>
        <td><strong>${totals.order_pcs || ''}</strong></td>
        <td><strong>${totals.order_kgs ? totals.order_kgs.toFixed(3) : ''}</strong></td>
        <td><strong>${totals.dispatched_pcs || ''}</strong></td>
        <td><strong>${totals.dispatched_kgs ? totals.dispatched_kgs.toFixed(3) : ''}</strong></td>
        <td><strong>${totals.packed_pcs || ''}</strong></td>
        <td><strong>${totals.packed_kgs ? totals.packed_kgs.toFixed(3) : ''}</strong></td>
        ${activeFilter !== 'die' ? `
          <td><strong>${totals.warehouse_pcs || ''}</strong></td>
          <td><strong>${totals.warehouse_kgs ? totals.warehouse_kgs.toFixed(3) : ''}</strong></td>
        ` : ''}
        <td><strong>${totals.pending_pcs || ''}</strong></td>
        <td><strong>${totals.pending_kgs ? totals.pending_kgs.toFixed(3) : ''}</strong></td>
        <td></td>
      </tr>
    </tfoot>
  `;
  };
  const groupData = (data) => {
    return data.reduce((acc, row) => {
      const key = row.workorder_no;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(row);
      return acc;
    }, {});
  };

  const groupedData = orderData.reduce((acc, row) => {
    if (!acc[row.workorder_no]) {
      acc[row.workorder_no] = [];
    }
    acc[row.workorder_no].push(row);
    return acc;
  }, {});



  const total_summary = {
    order_pcs: 0,
    order_kgs: 0,
    dispatched_pcs: 0,
    dispatched_kgs: 0,
    packed_pcs: 0,
    packed_kgs: 0,
    warehouse_pcs: 0,
    warehouse_kgs: 0,
    pending_pcs: 0,
    pending_kgs: 0,
  };

  Object.values(groupedData).forEach(rows => {
    rows.forEach(row => {
      total_summary.order_pcs += row.order_pcs || 0;
      total_summary.order_kgs += row.order_kgs || 0;
      total_summary.dispatched_pcs += row.dispatched_pcs || 0;
      total_summary.dispatched_kgs += row.dispatched_kgs || 0;
      total_summary.packed_pcs += row.packed_pcs || 0;
      total_summary.packed_kgs += row.packed_kgs || 0;
      total_summary.warehouse_pcs += row.warehouse_pcs || 0;
      total_summary.warehouse_kgs += row.warehouse_kgs || 0;
      total_summary.pending_pcs += row.pending_pcs || 0;
      total_summary.pending_kgs += row.pending_kgs || 0;
    });
  });

  const handleTableChange = (pagination, filters, sorter) => {
    setPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  return (
    <>
      <div className="sticyaction">
        <div className="pagenamerow ">
          <h2>Work Order Report</h2>
          <div className="bredcrumbwrp">
            <Link onClick={() => navigate(-1)} className="back">
              <Back /> BACK
            </Link>
            <Breadcrumb
              items={[
                { title: <Link to="/dashboard">Home</Link> },
                { title: <Link to="/work-order-list">Work Orders</Link> },
                { title: 'Work Order Report' },
              ]}
            />
          </div>
        </div>


      </div>
      <div className='formwrapper'>
        <Row gutter={16} className="no-print">
          <Col xs={24} sm={12} xl={12} xxl={12}>
            <div
              className="widwrp"
              style={{
                background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                border: '1px solid #c4b5fd',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              }}
            >
              <h3
                style={{
                  ...styles.filterTitle,
                  color: '#1e293b',
                  marginBottom: '16px',
                }}
              >
                Select Customer
              </h3>
              <div style={styles.filterInputContainer}>
                <AutoOpenSelect
                  placeholder="Please Select Customer Name"
                  onChange={handleCustomerChange}
                  value={
                    selectedCustomerId
                      ? { key: selectedCustomerId, label: selectedCustomerName }
                      : null
                  }
                  showSearch
                  onSearch={handleSearchCustomer}
                  style={{ width: '70%' }}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  disabled={activeFilter === 'die' && selectedDieId}
                  allowClear
                  onClear={handleClearCustomer}
                  labelInValue
                >
                  {[
                    ...new Map(customerList.map(item => [item.id, item])).values(),
                  ].map(el => (
                    <Option key={el.id} value={el.id} disabled={el.deleted === 1}>
                      {el.customer_name}
                    </Option>
                  ))}
                </AutoOpenSelect>
                <Button
                  onClick={handleViewClick}
                  type="primary"
                  loading={btnLoading}
                  disabled={!selectedCustomerId}
                  style={{
                    background: '#12406a',
                    color: 'white',
                    height: '36px',
                    borderRadius: '6px',
                  }}
                >
                  View
                </Button>
              </div>
            </div>
          </Col>
          <Col xs={24} sm={12} xl={12} xxl={12}>
            <div
              className="widwrp"
              style={{
                background: 'linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 100%)',
                border: '1px solid #c4b5fd',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              }}
            >
              <h3
                style={{
                  ...styles.filterTitle,
                  marginBottom: '16px',
                }}
              >
                Select Profile No
              </h3>
              <div style={styles.filterInputContainer}>
                <AutoOpenSelect
                  placeholder="Select Profile No"
                  style={{ width: '70%' }}
                  onChange={handleDieProfileChange}
                  open={forceOpen || undefined}
                  labelInValue={true}
                  loading={dieProfileLoading}
                  onPopupScroll={onScrollDieProfile}
                  onDropdownVisibleChange={open => {
                    if (open) {
                      setForceOpen(true);
                      setShowDropdownLoader(true);
                      setTimeout(() => {
                        setShowDropdownLoader(false);
                        setForceOpen(false);
                      }, 2000);
                    }
                  }}
                  onClear={handleClearDie}
                  value={searchDieProfile ? { value: searchDieProfile } : undefined}
                  notFoundContent={null}
                  showSearch={true}
                  onSearch={handleSearchDieProfile}
                  allowClear
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  dropdownRender={menu => (
                    <>
                      {showDropdownLoader ? (
                        <div style={{ padding: '16px', textAlign: 'center' }}>
                          <Spin size="small" />
                        </div>
                      ) : (
                        menu
                      )}
                    </>
                  )}
                >
                  {showDropdownLoader
                    ? [
                      <Option key="loading" disabled>
                        Loading...
                      </Option>,
                    ]
                    : [
                      ...new Map(
                        dieProfileDropdown.map(item => [item.id, item]),
                      ).values(),
                    ].map((el, index) => (
                      <Option
                        key={el.id}
                        value={el.id}
                        disabled={el.deleted === 1}
                      >
                        {el.die_number}
                      </Option>
                    ))}
                </AutoOpenSelect>
                <Button
                  onClick={handleViewClick}
                  type="primary"
                  loading={btnLoading}
                  disabled={!selectedDieId}
                  style={{
                    background: '#12406a',
                    color: 'white',
                    height: '36px',
                    borderRadius: '6px',
                  }}
                >
                  View
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </div>
      {((activeFilter === 'die' && selectedDieId) || (activeFilter === 'customer' && selectedCustomerId)) && (
        <div
          style={{
            textAlign: 'center',
            padding: '16px 24px',
            background: 'linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 100%)',
            border: '1px solid #cbd5e1',
            borderRadius: '8px',
            marginBottom: '20px',
            marginLeft: '20px',
            marginRight: '20px',
            position: 'relative',
          }}

        >
          <div style={{ fontSize: '14px', color: '#475569' }}>
            Showing Results For:{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, #1e40af 0%, #3730a3 100%)',
                color: 'white',
                padding: '6px 12px',
                borderRadius: '6px',
                fontWeight: '600',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            >
              {activeFilter === 'customer' ? selectedCustomerName : selectedDieName}
            </span>
          </div>
          <div
            style={{
              position: 'absolute',
              right: '24px',
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          >
            <Button
              type="primary"
              onClick={handlePrint}
              disabled={orderData.length === 0}
              style={{
                background: '#12406a',
                height: '38px',
                color: 'white',
                borderRadius: '6px',
                fontWeight: '600',
              }}
            >
              Print Report
            </Button>
          </div>
        </div>
      )}

      {((activeFilter === 'die' && selectedDieId) || (activeFilter === 'customer' && selectedCustomerId)) && (
        <div className="print-content">
          <div style={styles.tableContainer}>
            <div
              style={{
                maxHeight: '600px',
                overflowY: 'auto',
                border: '1px solid #ccc',
              }}
            >
              <table
                style={styles.table}
                className="print-table"
                onChange={handleTableChange}
              >
                <thead>
                  <tr style={styles.tableHeaderRow}>
                    <th style={styles.tableHeaderCell} rowSpan={2}>
                      Sr. No.
                    </th>
                    {activeFilter === 'die' && (
                      <th style={styles.tableHeaderCell} rowSpan={2}>
                        Customer Name
                      </th>
                    )}
                    <th style={styles.tableHeaderCell} rowSpan={2}>
                      Work Order No
                    </th>
                    <th style={styles.tableHeaderCell} rowSpan={2}>
                      Work Order Date - PO NO
                    </th>
                    <th style={styles.tableHeaderCell} rowSpan={2}>
                      Profile No
                    </th>
                    <th style={styles.tableHeaderCell} rowSpan={2}>
                      Profile Image
                    </th>

                    <th style={styles.tableHeaderCell} rowSpan={2}>
                      <> Customer<br /> Reference<br /> Number</>
                    </th>

                    <th style={styles.tableHeaderCell} rowSpan={2}>
                      Length(mm)
                    </th>
                    <th colSpan={2} style={styles.tableHeaderCell}>
                      Order
                    </th>
                    <th colSpan={2} style={styles.tableHeaderCell}>
                      Dispatched
                    </th>
                    <th colSpan={2} style={styles.tableHeaderCell}>
                      Packed
                    </th>
                    {activeFilter !== 'die' && (
                      <th colSpan={2} style={styles.tableHeaderCell}>
                        Warehouse Packed
                      </th>
                    )}
                    <th colSpan={2} style={styles.tableHeaderCell}>
                      Pending
                    </th>
                    <th style={styles.tableHeaderCell} rowSpan={2}>
                      Remarks
                    </th>
                  </tr>
                  <tr style={styles.tableHeaderRow}>
                    <th style={styles.tableHeaderCell}>PCS</th>
                    <th style={styles.tableHeaderCell}>KGS</th>
                    <th style={styles.tableHeaderCell}>PCS</th>
                    <th style={styles.tableHeaderCell}>KGS</th>
                    <th style={styles.tableHeaderCell}>PCS</th>
                    <th style={styles.tableHeaderCell}>KGS</th>
                    {activeFilter !== 'die' && (
                      <>
                        <th style={styles.tableHeaderCell}>PCS</th>
                        <th style={styles.tableHeaderCell}>KGS</th>
                      </>
                    )}
                    <th style={styles.tableHeaderCell}>PCS</th>
                    <th style={styles.tableHeaderCell}>KGS</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan={activeFilter === 'die' ? 17 : 19}
                        style={styles.loadingCell}
                      >
                        Loading data...
                      </td>
                    </tr>
                  ) : Object.keys(groupedData).length === 0 ? (
                    <tr>
                      <td
                        colSpan={activeFilter === 'die' ? 17 : 19}
                        style={styles.loadingCell}
                      >
                        No data available
                      </td>
                    </tr>
                  ) : (
                    (() => {
                      let srNo = 1;
                      return (
                        <>
                          {Object.entries(groupedData).map(([workorder_no, rows]) =>
                            rows.map((row, idx) => (
                              <tr
                                key={row.id}
                                style={
                                  srNo % 2 === 0
                                    ? styles.tableRowEven
                                    : styles.tableRowOdd
                                }
                              >
                                {idx === 0 && (
                                  <td style={styles.tableDataCell} rowSpan={rows.length}>
                                    {srNo++}
                                  </td>
                                )}

                                {idx === 0 && activeFilter === 'die' && (
                                  <td style={styles.tableDataCell} rowSpan={rows.length}>
                                    {row.customer_name || 'N/A'}
                                  </td>
                                )}

                                {idx === 0 && (
                                  <>
                                    <td style={styles.tableDataCell} rowSpan={rows.length}>
                                      {row.workorder_no}
                                    </td>
                                    <td style={styles.tableDataCell} rowSpan={rows.length}>
                                      {row.workorder_date}
                                    </td>
                                  </>
                                )}

                                <td style={styles.tableDataCell}>
                                  {row.section || ''}
                                </td>

                                <td style={styles.tableDataCell}>
                                  {row.profile_image !== '' && (<img
                                    width="70"
                                    src={row.profile_image || ''}
                                    alt=""
                                    style={{ objectFit: 'contain', maxHeight: '100%' }}
                                  />)}
                                </td>

                                <td style={styles.tableDataCell} >
                                  {row.customer_reference_number || ''}
                                </td>
                                <td style={styles.tableDataCell}>
                                  {row.length || ''}
                                </td>
                                <td style={styles.tableDataCell}>
                                  {row.order_pcs || ''}
                                </td>
                                <td style={styles.tableDataCell}>
                                  {row.order_kgs ? row.order_kgs.toFixed(3) : ''}
                                </td>
                                <td style={styles.tableDataCell}>
                                  {row.dispatched_pcs || ''}
                                </td>
                                <td style={styles.tableDataCell}>
                                  {row.dispatched_kgs ? row.dispatched_kgs.toFixed(3) : ''}
                                </td>
                                <td style={styles.tableDataCell}>
                                  {row.packed_pcs || ''}
                                </td>
                                <td style={styles.tableDataCell}>
                                  {row.packed_kgs ? row.packed_kgs.toFixed(3) : ''}
                                </td>

                                {activeFilter !== 'die' && (
                                  <>
                                    <td style={styles.tableDataCell}>
                                      {row.warehouse_pcs || ''}
                                    </td>
                                    <td style={styles.tableDataCell}>
                                      {row.warehouse_kgs ? row.warehouse_kgs.toFixed(3) : ''}
                                    </td>
                                  </>
                                )}

                                <td style={styles.tableDataCell}>
                                  {row.pending_pcs || ''}
                                </td>
                                <td style={styles.tableDataCell}>
                                  {row.pending_kgs ? row.pending_kgs.toFixed(3) : ''}
                                </td>
                                <td style={styles.tableDataCell}>
                                  {row.remark || ''}
                                </td>
                              </tr>
                            ))
                          )}
                          <tr style={styles.totalRow}>
                            <td colSpan={activeFilter === 'die' ? 8 : 7} style={styles.tableDataCell}>
                              <strong>Total</strong>
                            </td>
                            <td style={styles.tableDataCell}>
                              <strong>{total_summary.order_pcs || ''}</strong>
                            </td>
                            <td style={styles.tableDataCell}>
                              <strong>{total_summary.order_kgs ? total_summary.order_kgs.toFixed(3) : ''}</strong>
                            </td>
                            <td style={styles.tableDataCell}>
                              <strong>{total_summary.dispatched_pcs || ''}</strong>
                            </td>
                            <td style={styles.tableDataCell}>
                              <strong>{total_summary.dispatched_kgs ? total_summary.dispatched_kgs.toFixed(3) : ''}</strong>
                            </td>
                            <td style={styles.tableDataCell}>
                              <strong>{total_summary.packed_pcs || ''}</strong>
                            </td>
                            <td style={styles.tableDataCell}>
                              <strong>{total_summary.packed_kgs ? total_summary.packed_kgs.toFixed(3) : ''}</strong>
                            </td>
                            {activeFilter !== 'die' && (
                              <>
                                <td style={styles.tableDataCell}>
                                  <strong>{total_summary.warehouse_pcs || ''}</strong>
                                </td>
                                <td style={styles.tableDataCell}>
                                  <strong>{total_summary.warehouse_kgs ? total_summary.warehouse_kgs.toFixed(3) : ''}</strong>
                                </td>
                              </>
                            )}
                            <td style={styles.tableDataCell}>
                              <strong>{total_summary.pending_pcs || ''}</strong>
                            </td>
                            <td style={styles.tableDataCell}>
                              <strong>{total_summary.pending_kgs ? total_summary.pending_kgs.toFixed(3) : ''}</strong>
                            </td>
                            <td style={styles.tableDataCell}></td>
                          </tr>
                        </>
                      );
                    })()
                  )}
                </tbody>

              </table>
            </div>
            {/* </div> */}
          </div>
        </div>
      )}
    </>
  );
};

export default WorkOrderReport;
